import { ProductCard } from './ProductCard';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoadingSpinner, ErrorMessage, Breadcrumb } from './ui';
import { BreadcrumbItem } from '../types';
import { useProducts } from '../hooks/useProducts';
import { useShopping } from '../contexts/ShoppingContext';
import { useClientData } from '../hooks/useClientData';
import { useEffect, useRef, useCallback, useState } from 'react';
import { Product } from "../types";

const PRODUCTS_PER_PAGE = 30;

export function ProductGrid() {
  const location = useLocation();
  const navigate = useNavigate();
  const lastDispatchedProductsRef = useRef<string>('');
  const isInitialLoad = useRef(true);

  const [allProducts, setAllProducts] = useState<Product[]>([]);

  // Get client data for default sort
  const { clientData } = useClientData();

  // Get filters from URL
  const urlParams = new URLSearchParams(location.search);
  const selectedCategoryId = urlParams.get('categoryId') ? Number(urlParams.get('categoryId')) : undefined;
  const selectedDepartmentId = urlParams.get('departmentId') ? Number(urlParams.get('departmentId')) : undefined;
  const searchQuery = urlParams.get('search') || undefined;
  const sortFromUrl = urlParams.get('sort') || clientData?.defaultProductSort || 'prioritized';

  // Get current page from URL or use 1
  const currentPage = urlParams.get('page') ? Number(urlParams.get('page')) : 1;

  // On first load, load all pages up to current
  const size = isInitialLoad.current ? currentPage * PRODUCTS_PER_PAGE : PRODUCTS_PER_PAGE;

  // Hook loads products
  const { products: loadedProducts, loading, error, totalPages, total } = useProducts({
    departmentId: selectedDepartmentId,
    categoryId: selectedCategoryId,
    search: searchQuery,
    page: isInitialLoad.current ? 1 : currentPage,
    sort: sortFromUrl,
    enabled: true,
    size: size,
  });

  // When data loads, update all products
  useEffect(() => {
    if (!loading && loadedProducts.length > 0) {
      setAllProducts(prev => {
        if (isInitialLoad.current) {
          // On first load replace all
          isInitialLoad.current = false;
          return loadedProducts;
        } else {
          // On subsequent loads - add new products
          const existingIds = new Set(prev.map(p => p.id));
          const newProducts = loadedProducts.filter(p => !existingIds.has(p.id));
          return [...prev, ...newProducts];
        }
      });
    }
  }, [loadedProducts, loading, currentPage]);

  // Handle View More click
  const handleLoadMore = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const nextPage = currentPage + 1;
    searchParams.set('page', nextPage.toString());
    navigate(`/?${searchParams.toString()}`);
  }, [location.search, navigate, currentPage]);

  // Reset when filters change
  useEffect(() => {
    setAllProducts([]);
    isInitialLoad.current = true;
  }, [selectedCategoryId, selectedDepartmentId, searchQuery, sortFromUrl]);

  // Handle sort change
  const handleSortChange = useCallback((newSort: string) => {
    const defaultSort = clientData?.defaultProductSort || 'prioritized';
    const searchParams = new URLSearchParams(location.search);
    if (newSort === defaultSort) {
      searchParams.delete('sort');
    } else {
      searchParams.set('sort', newSort);
    }
    searchParams.delete('page');
    navigate(`/?${searchParams.toString()}`);
  }, [location.search, navigate, clientData?.defaultProductSort]);

  // Get context for departments and dispatch
  const { state, dispatch } = useShopping();

  // Get department and category names for display
  const selectedDepartment = state.departments?.find(d => d.id === selectedDepartmentId);
  const selectedCategory = state.departments
      ?.flatMap(d => d.productCategories || [])
      .find(c => c.id === selectedCategoryId);

  // Build breadcrumb items
  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Home', href: '/' }
  ];

  if (selectedDepartment) {
    breadcrumbItems.push({
      label: selectedDepartment.name,
      href: `/?departmentId=${selectedDepartment.id}`
    });
  }

  if (selectedCategory) {
    const categoryHref = selectedDepartment
        ? `/?departmentId=${selectedDepartment.id}&categoryId=${selectedCategory.id}`
        : `/?categoryId=${selectedCategory.id}`;

    breadcrumbItems.push({
      label: selectedCategory.name,
      href: categoryHref
    });
  }

  // Sync all products back to context
  useEffect(() => {
    if (!loading && allProducts.length > 0) {
      const productsKey = JSON.stringify(allProducts.map(p => p.id));
      if (productsKey !== lastDispatchedProductsRef.current) {
        lastDispatchedProductsRef.current = productsKey;
        dispatch({ type: 'SET_PRODUCTS', payload: allProducts });
      }
    }
  }, [loading, allProducts, dispatch]);

  // Show loading only on first load
  const showInitialLoading = loading && allProducts.length === 0;

  if (showInitialLoading) {
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner size="lg" text="Loading products..." />
        </div>
    );
  }

  if (error && allProducts.length === 0) {
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage
              title="Failed to load products"
              message={error}
              onRetry={() => window?.location.reload()}
          />
        </div>
    );
  }

  return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={breadcrumbItems} />
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {searchQuery
                ? `Search Results for "${searchQuery}"`
                : selectedCategory
                    ? `${selectedCategory.name} Products`
                    : selectedDepartment
                        ? `${selectedDepartment.name} Department`
                        : 'Featured Products'
            }
          </h1>
          <p className="text-gray-600">
            {searchQuery
                ? `Found ${total} ${total === 1 ? 'product' : 'products'} matching your search`
                : selectedCategory
                    ? `Explore our ${selectedCategory.name.toLowerCase()} collection`
                    : selectedDepartment
                        ? `Browse all products in the ${selectedDepartment.name} department`
                        : 'Discover our curated selection of premium products'
            }
          </p>
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label htmlFor="sort-select" className="text-sm text-gray-700 font-medium">
                  Sort by:
                </label>
                <select
                    id="sort-select"
                    value={sortFromUrl}
                    onChange={(e) => handleSortChange(e.target.value)}
                    className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="prioritized">Prioritized</option>
                  <option value="alphabetical">Alphabetical</option>
                </select>
              </div>
              {totalPages > 1 && (
                  <span className="text-sm text-gray-500">
                Page {currentPage} of {totalPages}
              </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" role="list" aria-label="Product list">
          {allProducts.map(product => (
              <div key={product.id} role="listitem">
                <ProductCard product={product} />
              </div>
          ))}
        </div>

        {loading && allProducts.length > 0 && (
            <div className="mt-8 flex justify-center">
              <LoadingSpinner size="md" text="Loading more products..." />
            </div>
        )}

        {allProducts.length === 0 && !loading && (
            <div className="text-center py-12" role="status">
              <div className="text-gray-500 text-lg mb-2">No products found</div>
              <p className="text-gray-400">
                {searchQuery
                    ? `No products match your search for "${searchQuery}". Try different keywords.`
                    : selectedCategory
                        ? `No products available in the ${selectedCategory.name} category.`
                        : selectedDepartment
                            ? `No products available in the ${selectedDepartment.name} department.`
                            : 'No products available at the moment.'
                }
              </p>
            </div>
        )}

        {/* Show View More button when there are more pages */}
        {currentPage < totalPages && !loading && (
            <div className="mt-12 flex justify-center">
              <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                View More ({allProducts.length} of {total})
              </button>
            </div>
        )}
      </div>
  );
}